// Agent activities (MUI, /agents). Presentation port of AgentsPage — same data
// and mutations, rebuilt with mui/* primitives + brainTokens under MuiAppShell.
import { useState, useEffect, useCallback, useRef } from "react";
import { Box, Button, Skeleton, TextField } from "@mui/material";
import { listAgents, getBusActivity, createAgent, revokeAgent, rotateAgentKey } from "../../lib/brain-api";
import type { Agent, BusActivity } from "../../lib/brain-types";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, SectionCard, StatusPill, MonoLabel, Icon } from "../../mui";

const c = brainTokens.color;

const STATUS_TONE: Record<string, "teal" | "neutral" | "danger" | "warning"> = {
  working: "teal",
  idle: "neutral",
  error: "danger",
  stale: "warning",
  unknown: "neutral",
};

function relTime(ts: string | null): string {
  if (!ts) return "never";
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function parseCreateResponse(text: string): { apiKey: string; cliSnippet: string } | null {
  const keyMatch = text.match(/API Key:\s*(ob_\S+)/);
  const cliMatch = text.match(/claude mcp add[^\n]+/);
  if (!keyMatch) return null;
  return {
    apiKey: keyMatch[1],
    cliSnippet: cliMatch ? cliMatch[0].trim() : "",
  };
}

interface NewKeyInfo {
  agentName: string;
  apiKey: string;
  cliSnippet: string;
  action: "created" | "rotated";
}

export function AgentsPageMui() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [activity, setActivity] = useState<BusActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isLoadingRef = useRef(false);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<NewKeyInfo | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCli, setCopiedCli] = useState(false);

  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);

  const load = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const [agentList, bus] = await Promise.all([listAgents(), getBusActivity({ hours: 24, limit: 20 })]);
      setAgents(agentList);
      setActivity(bus);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      load();
    }, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError(null);
    try {
      const response = await createAgent(name);
      const parsed = parseCreateResponse(response);
      if (!parsed) {
        setCreateError(response.startsWith("Error:") ? response : "Unexpected response from server.");
        return;
      }
      setNewKey({ agentName: name, ...parsed, action: "created" });
      setNewName("");
      setShowCreate(false);
      await load();
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (name: string) => {
    setRevoking(true);
    try {
      await revokeAgent(name);
      setConfirmRevoke(null);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRevoking(false);
    }
  };

  const handleRotate = async (name: string) => {
    setRotating(true);
    try {
      const response = await rotateAgentKey(name);
      const parsed = parseCreateResponse(response);
      if (!parsed) {
        setError(response.startsWith("Error:") ? response : "Unexpected response from server.");
        return;
      }
      setNewKey({ agentName: name, ...parsed, action: "rotated" });
      setConfirmRotate(null);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRotating(false);
    }
  };

  const copyToClipboard = (text: string, which: "key" | "cli") => {
    const set = which === "key" ? setCopiedKey : setCopiedCli;
    navigator.clipboard.writeText(text).then(() => {
      set(true);
      setTimeout(() => set(false), 2000);
    });
  };

  const activeCount = agents.filter((a) => a.status === "working").length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Agent activities"
        eyebrow={
          <MonoLabel color={c.muted} size={11} letterSpacing="0.1em">
            {loading ? "Loading…" : `${activeCount} active · ${agents.length} registered`}
          </MonoLabel>
        }
        actions={
          <>
            <Button
              variant="contained"
              size="small"
              startIcon={<Icon name="key" size={16} />}
              onClick={() => {
                setShowCreate((v) => !v);
                setCreateError(null);
                setNewName("");
              }}
            >
              {showCreate ? "Cancel" : "New agent"}
            </Button>
            <Button size="small" startIcon={<Icon name="refresh" size={16} />} onClick={load} disabled={loading}>
              {loading ? "…" : "Refresh"}
            </Button>
          </>
        }
      />

      {error && (
        <Box sx={{ fontSize: 13, color: c.dangerText, bgcolor: c.dangerBg, border: `1px solid ${c.dangerBorder}`, borderRadius: `${brainTokens.radius.button}px`, p: 1.5 }}>
          {error}
        </Box>
      )}

      {showCreate && (
        <SectionCard label="New agent">
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              size="small"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void handleCreate()}
              placeholder="agent-name (alphanumeric, hyphens, underscores)"
              disabled={creating}
              autoFocus
              sx={{ flex: 1, minWidth: 200 }}
            />
            <Button variant="contained" onClick={() => void handleCreate()} disabled={creating || !newName.trim()}>
              {creating ? "Creating…" : "Create"}
            </Button>
          </Box>
          {createError && <Box sx={{ fontSize: 12, color: c.dangerText, mt: 1 }}>{createError}</Box>}
        </SectionCard>
      )}

      {newKey && (
        <SectionCard
          label={`Agent "${newKey.agentName}" ${newKey.action === "rotated" ? "key rotated" : "created"}`}
          actions={
            <Button size="small" startIcon={<Icon name="x" size={14} />} onClick={() => setNewKey(null)}>
              Dismiss
            </Button>
          }
        >
          <Box sx={{ fontSize: 12, color: c.muted, mb: 1.5 }}>Copy your API key now — it will not be shown again.</Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Box
              component="code"
              sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: `${brainTokens.radius.button}px`,
                p: "8px 14px",
                fontSize: 12,
                fontFamily: brainTokens.font.mono,
                overflowWrap: "anywhere",
                bgcolor: c.panelBg2,
                color: c.tealInk,
              }}
            >
              {newKey.apiKey}
            </Box>
            <Button size="small" startIcon={<Icon name={copiedKey ? "check" : "copy"} size={14} />} onClick={() => copyToClipboard(newKey.apiKey, "key")}>
              {copiedKey ? "Copied!" : "Copy"}
            </Button>
          </Box>
          {newKey.cliSnippet && (
            <Box sx={{ mt: 2 }}>
              <MonoLabel color={c.muted} size={11} letterSpacing="0.1em" sx={{ display: "block", mb: 0.5 }}>
                Claude Code setup
              </MonoLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Box
                  component="code"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: `${brainTokens.radius.button}px`,
                    p: "8px 14px",
                    fontSize: 12,
                    fontFamily: brainTokens.font.mono,
                    overflowWrap: "anywhere",
                    bgcolor: c.panelBg2,
                    color: c.ink3,
                  }}
                >
                  {newKey.cliSnippet}
                </Box>
                <Button size="small" startIcon={<Icon name={copiedCli ? "check" : "copy"} size={14} />} onClick={() => copyToClipboard(newKey.cliSnippet, "cli")}>
                  {copiedCli ? "Copied!" : "Copy"}
                </Button>
              </Box>
            </Box>
          )}
        </SectionCard>
      )}

      {loading && agents.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={96} />
          ))}
        </Box>
      ) : agents.length === 0 && !error ? (
        <Box sx={{ textAlign: "center", py: 4, fontSize: 13, color: c.muted }}>No agents registered yet.</Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {agents.map((agent) => {
            const tone = STATUS_TONE[agent.status] ?? "neutral";
            const isConfirming = confirmRevoke === agent.name;
            return (
              <SectionCard key={agent.name} padding={16}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        bgcolor: c.tealTint,
                      }}
                    >
                      <Icon name="robot" size={20} style={{ color: c.tealInk }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <MonoLabel color={c.tealInk} size={11} letterSpacing="0.1em" sx={{ fontWeight: 700, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {agent.name}
                      </MonoLabel>
                      <Box sx={{ mt: "2px" }}>
                        <StatusPill tone={tone} label={`${agent.status}${agent.statusMessage ? ` · ${agent.statusMessage}` : ""}`} />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0, flexWrap: "wrap" }}>
                    <Box sx={{ fontSize: 10, color: c.muted }}>{relTime(agent.lastSeen)}</Box>
                    {isConfirming ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ fontSize: 10, color: c.dangerText }}>Revoke?</Box>
                        <Button color="error" variant="contained" size="small" onClick={() => void handleRevoke(agent.name)} disabled={revoking}>
                          {revoking ? "…" : "Yes"}
                        </Button>
                        <Button size="small" onClick={() => setConfirmRevoke(null)} disabled={revoking}>
                          Cancel
                        </Button>
                      </Box>
                    ) : confirmRotate === agent.name ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ fontSize: 10, color: c.tealInk }}>Rotate key?</Box>
                        <Button variant="contained" size="small" onClick={() => void handleRotate(agent.name)} disabled={rotating}>
                          {rotating ? "…" : "Yes"}
                        </Button>
                        <Button size="small" onClick={() => setConfirmRotate(null)} disabled={rotating}>
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<Icon name="refresh" size={14} />}
                          onClick={() => {
                            setConfirmRotate(agent.name);
                            setConfirmRevoke(null);
                          }}
                        >
                          Rotate
                        </Button>
                        <Button
                          color="error"
                          variant="outlined"
                          size="small"
                          startIcon={<Icon name="trash" size={14} />}
                          onClick={() => {
                            setConfirmRevoke(agent.name);
                            setConfirmRotate(null);
                          }}
                        >
                          Revoke
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </SectionCard>
            );
          })}
        </Box>
      )}

      {activity && activity.recent.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <MonoLabel color={c.muted} size={11} letterSpacing="0.1em">
            Recent · {activity.summary.total} thoughts in {activity.summary.hours}h
          </MonoLabel>
          {activity.recent.map((item) => (
            <Box
              key={`${item.agent}-${item.created_at}-${item.content.slice(0, 16)}`}
              sx={{ borderRadius: `${brainTokens.radius.panel}px`, p: 2, display: "flex", flexDirection: "column", gap: 1, bgcolor: c.panelBg2 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <MonoLabel color={c.tealInk} size={11} letterSpacing="0.1em" sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item.agent}
                </MonoLabel>
                <Box sx={{ fontSize: 10, flexShrink: 0, color: c.muted }}>{relTime(item.created_at)}</Box>
              </Box>
              <Box
                sx={{
                  fontSize: 13,
                  color: c.ink3,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {item.content}
              </Box>
              {item.topics.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {item.topics.map((t) => (
                    <StatusPill key={t} tone="teal" label={t} />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
